import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Ban, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCancelMyLeaveMutation, useRequestMyLeaveMutation, useWorkplaceLeaveQuery } from '@/client/api/hr/people';
import { TIME_OFF_STATUS_BADGE, TIME_OFF_STATUS_LABEL, type TimeOffRequest } from '@/client/types/hr';
import { DeleteConfirmDialog, EmptyState, Panel, ProgressBar, RowMenu, StatusBadge } from '../HumanResource/components/hr-ui';
import { balanceLabel, formatDay, formatRange } from '../HumanResource/components/leave-format';

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

/** Mon-Fri count, the same rule the API applies — a preview, the API decides. */
function weekdays(start: string, end: string) {
  if (!start || !end || end < start) return 0;
  let n = 0;
  const d = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);
  for (; d <= last; d.setDate(d.getDate() + 1)) if (d.getDay() % 6 !== 0) n += 1;
  return n;
}

export function TimeOffTab() {
  const year = new Date().getFullYear();
  const leaveQ = useWorkplaceLeaveQuery(year);
  const request = useRequestMyLeaveMutation();
  const cancel = useCancelMyLeaveMutation();
  const [form, setForm] = useState({ typeId: '', startDate: '', endDate: '', reason: '' });
  const [cancelling, setCancelling] = useState<TimeOffRequest | null>(null);

  const data = leaveQ.data;
  const types = data?.types ?? [];
  const typeId = form.typeId || types[0]?.id || '';
  const balance = data?.balances.find((b) => b.typeId === typeId);
  const days = weekdays(form.startDate, form.endDate);
  const today = new Date().toISOString().slice(0, 10);
  const over = balance && balance.remaining !== null && days > balance.remaining;

  const requests = useMemo(() => data?.requests ?? [], [data]);
  const canCancel = (r: TimeOffRequest) => r.status === 'pending' || (r.status === 'approved' && r.startDate > today);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!typeId || !form.startDate || !form.endDate) {
      toast.error('Pick a type, a first day and a last day');
      return;
    }
    request.mutate(
      { typeId, startDate: form.startDate, endDate: form.endDate, reason: form.reason.trim() },
      {
        onSuccess: (r) => {
          toast.success(`Requested ${r.days} working day${r.days === 1 ? '' : 's'}. Your manager has been asked to decide.`);
          setForm({ typeId, startDate: '', endDate: '', reason: '' });
        },
        onError: (err) => toast.error((err as Error).message || 'Could not send the request'),
      },
    );
  };

  const confirmCancel = () => {
    if (!cancelling) return;
    cancel.mutate(cancelling.id, {
      onSuccess: () => toast.success('Request cancelled'),
      onError: (err) => toast.error((err as Error).message || 'Could not cancel the request'),
    });
    setCancelling(null);
  };

  if (leaveQ.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 bg-white py-14 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your time off…
      </div>
    );
  }
  if (leaveQ.isError || !data) {
    return <EmptyState label={`Could not load your time off: ${(leaveQ.error as Error)?.message ?? 'unknown error'}`} />;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Panel title={`My balances, ${data.year}`}>
        {data.balances.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">No leave types are set up yet.</p>
        ) : (
          <div className="space-y-3">
            {data.balances.map((b) => (
              <div key={b.typeId}>
                <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                  <span className="font-medium text-gray-700">{b.typeName}</span>
                  <span className="text-gray-500">{balanceLabel(b)}</span>
                </div>
                {b.allotted !== null && <ProgressBar value={b.allotted ? ((b.used + b.pending) / b.allotted) * 100 : 0} />}
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Request time off">
        <form onSubmit={submit} className="space-y-3">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-gray-600">Type</span>
            <select value={typeId} onChange={(e) => setForm((f) => ({ ...f, typeId: e.target.value }))} className={inputClass}>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">First day</span>
              <input
                type="date"
                value={form.startDate}
                min={today}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value, endDate: f.endDate || e.target.value }))}
                className={inputClass}
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Last day</span>
              <input
                type="date"
                value={form.endDate}
                min={form.startDate || today}
                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                className={inputClass}
              />
            </label>
          </div>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-gray-600">Note for your manager (optional)</span>
            <input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} className={inputClass} />
          </label>
          {days > 0 && (
            <p className={`text-xs ${over ? 'text-red-600' : 'text-gray-500'}`}>
              {days} working day{days === 1 ? '' : 's'}
              {balance && balance.remaining !== null && ` · ${balance.remaining} left`}
              {over && ' · more than you have left'}
            </p>
          )}
          <Button type="submit" className="w-full gap-2" disabled={request.isPending || !!over}>
            {request.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Send request
          </Button>
        </form>
      </Panel>

      <Panel title="My requests">
        {requests.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">You have not asked for time off yet.</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {requests.map((r) => (
              <li key={r.id} className="flex items-start justify-between gap-2 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm text-gray-800">
                    {r.typeName} · {r.days}d
                  </p>
                  <p className="text-xs text-gray-500">{formatRange(r)}</p>
                  {r.decisionNote && <p className="mt-0.5 text-xs text-gray-500">“{r.decisionNote}”</p>}
                  {r.decidedAt && <p className="text-[11px] text-gray-400">Decided {formatDay(r.decidedAt)}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <StatusBadge label={TIME_OFF_STATUS_LABEL[r.status]} className={TIME_OFF_STATUS_BADGE[r.status]} />
                  <RowMenu
                    label="Request actions"
                    items={[{ label: 'Cancel request', icon: Ban, danger: true, onSelect: () => setCancelling(r), hidden: !canCancel(r) }]}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <DeleteConfirmDialog
        open={!!cancelling}
        onOpenChange={(v) => !v && setCancelling(null)}
        title="Cancel this request?"
        description={
          cancelling ? `${cancelling.typeName}, ${formatRange(cancelling)}. The days go back to your balance.` : ''
        }
        actionLabel="Cancel request"
        onConfirm={confirmCancel}
      />
    </div>
  );
}
